/* eslint-disable prefer-const */
import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import {
  ISystemConfigData,
  ISystemMonitoringData,
} from "types/models/system-config";
import { formatPath, getQueryString } from "utils";
import { apiDelete, apiGet, apiPost, apiPut } from "utils/api";
import {
  closeAppBackdrop,
  notify,
  showAppBackdrop,
} from "features/app/store/slice";
import { IApiResponse } from "types/api";
import SecurityMonitoringConfigPaths from "../models/paths";
import {
  createLogMetadata,
  deleteLogId,
  deleteLogManagement,
  editDisplayLogManagement,
  editLogManagement,
  exportDataLogName,
  fetchLogMonitoring,
  fetchLogMonitoringFilter,
  fetchLogMonitoringSuccess,
  fetchSecurityMonitoring,
  fetchSecurityMonitoringSuccess,
  lasestUpdateMetadataSuccess,
  latestUpdateMetadata,
  reloadDataManagement,
  statusProgressJob,
  uploadFileLog,
  uploadFileLogSuccess,
} from "./slice";
import { ISecurityMonitoringData } from "types/models/security-monitoring";

//  ==============  Get Log Management  ==============
export const apiFetchMetadata = () => {
  return apiGet<ISecurityMonitoringData>(
    formatPath(SecurityMonitoringConfigPaths.get_log_metadata)
  );
};
function* handleFetchDataMetadata() {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiFetchMetadata);
    if (response.data) {
      yield put(fetchSecurityMonitoringSuccess(response.data.data));
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}
//  ============== End Get Log Management  ==============

//  ==============  Get Log  ==============
export const apiFetchLogData = () => {
  return apiGet(formatPath(SecurityMonitoringConfigPaths.get_log));
};

function* handleFetchDataLog() {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiFetchLogData);
    if (response?.data) {
      yield put(fetchLogMonitoringSuccess(response.data.data));
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchLogDataFilter = (data: Partial<any>) => {
  return apiGet(
    formatPath(SecurityMonitoringConfigPaths.get_log) +
      `?start_date=${data.start_date}&end_date=${data.end_date}`
  );
};

function* handleFetchDataLogFilter(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiFetchLogDataFilter,
      action.payload
    );
    if (response?.data) {
      yield put(fetchLogMonitoringSuccess(response.data.data));
      yield put(closeAppBackdrop());
    } else {
      yield put(
        notify(`${response.detail}`, {
          options: { variant: "error" },
        })
      );
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

//  ==============  End get Log  ==============

// ============== Reload data  ==============
export const apiFetchLogReloadData = () => {
  return apiGet(formatPath(SecurityMonitoringConfigPaths.get_log));
};
function* handleReloadData() {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(apiFetchLogData);
    if (response?.data) {
      yield put(fetchLogMonitoringSuccess(response.data.data));
      yield call(handleFetchDataMetadata);
      yield call(handleLatestUpdate);
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}
//  ============== END Reload Data  ==============

//  ==============  EditLogManagement Detail  ==============
export const apiEditLogManagement = (data: any) => {
  return apiPut<any>(
    formatPath(SecurityMonitoringConfigPaths.update_metadata_log_name) +
      data.logName.log_name,
    data.payload
  );
};
function* handleUpdateEditLogManagement(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(
      apiEditLogManagement,
      action.payload
    );

    if (response.data) {
      yield put(
        notify(`${response.data.data.log_name} updated successfully`, {
          options: { variant: "success" },
        })
      );
      yield call(handleFetchDataMetadata);
      yield call(handleFetchDataLog);
      yield put(closeAppBackdrop());
    } else {
      if (response.data !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiEditDisplayLogManagement = (data: any) => {
  const log_name = data.log_name;
  const disable = data.disable;
  return apiPut<any>(
    formatPath(SecurityMonitoringConfigPaths.update_metadata_log_name) +
      log_name +
      `?disable=${disable}`,
    data.payload
  );
};
function* handleUpdateEditDisplayLogManagement(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(
      apiEditDisplayLogManagement,
      action.payload
    );
    if (response.data) {
      yield put(
        notify(`${response.data.data.log_name} updated successfully`, {
          options: { variant: "success" },
        })
      );
      yield call(handleFetchDataMetadata);
      yield call(handleFetchDataLog);
      yield put(closeAppBackdrop());
    } else {
      if (response.data !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}
//  ============== End EditLogManagement Detail  ==============

//  ============== DELETE ==============
export const apiDeleteMetadataLogName = (logName: Partial<any>) => {
  return apiDelete<any>(
    formatPath(SecurityMonitoringConfigPaths.delete_log_metadata) + logName
  );
};
function* handleDeleteMetadataLogName(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiDeleteMetadataLogName,
      action.payload
    );

    if (response.data) {
      yield put(
        notify(`${action.payload} deleted successfully`, {
          options: { variant: "success" },
        })
      );
      yield call(handleFetchDataMetadata);
      yield call(handleFetchDataLog);
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (error) {
    yield put(closeAppBackdrop());
  }
}
//  ============== END DELETE ==============

// ============== DELETE ID ==============
export const apiDeleteLogById = (data: Partial<any>) => {
  return apiDelete<any>(
    formatPath(SecurityMonitoringConfigPaths.delete_log) + data.log_name,
    {
      log_ids: data.log_ids,
    }
  );
};

function* handleDeleteLogById(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiDeleteLogById,
      action.payload
    );
    if (response.data) {
      yield put(
        notify(`${action.payload.log_name} deleted successfully`, {
          options: { variant: "success" },
        })
      );
      yield call(handleFetchDataLog);
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (error) {
    yield put(closeAppBackdrop());
  }
}

// ============== END DELETE ID ==============

// ============== Export ==============
export const apiExportLogName = (data: Partial<any>) => {
  return apiPost<any>(
    formatPath(SecurityMonitoringConfigPaths.export_log_name) + data.name,
    data,
    {},
    {
      responseType: "blob",
    }
  );
};

function* handleExportLogName(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiExportLogName,
      action.payload
    );
    if (response.data) {
      yield put(
        notify(
          `Export file ${action.payload.name} successfully. The password is sent to your email. Please check it out to unzip the file`,
          {
            options: { variant: "success" },
          }
        )
      );
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const filename = action.payload.name + `.zip`;
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      yield put(closeAppBackdrop());
    } else {
      yield put(
        notify(`Export error ${action.payload.name}`, {
          options: { variant: "error" },
        })
      );
      yield put(closeAppBackdrop());
    }
  } catch (error) {
    yield put(closeAppBackdrop());
  }
}
// ============== End Export ==============

// ============== Upload File ==============
export const apiUploadFile = (data: Partial<any>) => {
  return apiPost<any>(
    formatPath(SecurityMonitoringConfigPaths.upload_log_file),
    data
  );
};

function* handleUploadFile(action: Partial<any>) {
  try {
    yield put(showAppBackdrop());

    const formData = action.payload.formData;
    const response: IApiResponse<any> = yield call(
      apiUploadFile,
      formData,
    );
    if (response.data) {
      yield put(
        notify(`${action.payload.log_name} uploaded successfully`, { options: { variant: "success" } })
      );
      const recordCreate = {
        log_name: action.payload.log_name,
        column_name: response.data.data,
      };
      yield put(uploadFileLogSuccess(recordCreate));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (error) {
    yield put(
      notify(`Failed to upload file`, { options: { variant: "error" } })
    );
    yield put(closeAppBackdrop());
  }
}

// ============== End Upload File ==============

export const apiCheckJobStatus = (data: any) => {
  const path = SecurityMonitoringConfigPaths.job_status.replace(":id", data);
  return apiGet<any>(formatPath(path));
};

function* pollJobStatus(jobId: string): Generator<any, void, any> {
  try {
    yield put(closeAppBackdrop());
    const response: IApiResponse<any> = yield call(apiCheckJobStatus, jobId);
    yield put(statusProgressJob(0));
    const status = response.data?.state;

    if (status === "success") {
      yield put(showAppBackdrop());
      yield put(
        notify(`${response.data?.log_name} created successfully`, {
          options: { variant: "success" },
        })
      );
      yield put(statusProgressJob(null));
    }else {
      const process = ((response.data?.no_process_row / response.data?.no_total_row) * 100).toFixed(1);
      yield put(statusProgressJob(process));
      yield call(pollJobStatus, jobId);
    }
  } catch (error) {
    yield put(statusProgressJob(null));
    yield put(
      notify(`Job status failed!`, {
        options: { variant: "error" },
      })
    );
  }
}


//  ============== Create log ==============
export const apiCreateLogManagement = (data: any) => {
  return apiPost<any>(
    formatPath(SecurityMonitoringConfigPaths.create_metadata),
    data
  );
};

function* handleCreateLogManagement(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiCreateLogManagement,
      action.payload
    );

    if (response.data) {
      yield call(pollJobStatus, response.data.data.job_id);
      yield call(handleFetchDataMetadata);
      yield call(handleFetchDataLog);
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (error) {
    yield put(notify(`Create failed!`, { options: { variant: "error" } }));
    yield put(closeAppBackdrop());
  }
}
//  ============== End Create log ==============

//  ============== Lastest Update ==============
export const apiLastestUpdate = () => {
  return apiGet<any>(formatPath(SecurityMonitoringConfigPaths.lastest_update));
};

function* handleLatestUpdate() {
  try {
    yield put(showAppBackdrop);
    const response: IApiResponse<any> = yield call(apiLastestUpdate);
    if (response.data) {
      yield put(lasestUpdateMetadataSuccess(response.data));
      yield put(closeAppBackdrop());
    }
  } catch (error) {
    yield put(closeAppBackdrop());
  }
}
//  ============== End Lastest Update ==============

export default function* SecurityMonitoringSaga() {
  yield takeEvery(fetchSecurityMonitoring.type, handleFetchDataMetadata);
  yield takeEvery(fetchLogMonitoring.type, handleFetchDataLog);
  yield takeEvery(fetchLogMonitoringFilter.type, handleFetchDataLogFilter);
  yield takeEvery(editLogManagement.type, handleUpdateEditLogManagement);
  yield takeEvery(reloadDataManagement.type, handleReloadData);
  yield takeEvery(deleteLogManagement.type, handleDeleteMetadataLogName);
  yield takeEvery(exportDataLogName.type, handleExportLogName);
  yield takeEvery(uploadFileLog.type, handleUploadFile);
  yield takeEvery(deleteLogId.type, handleDeleteLogById);
  yield takeEvery(createLogMetadata.type, handleCreateLogManagement);
  yield takeEvery(latestUpdateMetadata.type, handleLatestUpdate);
  yield takeEvery(
    editDisplayLogManagement,
    handleUpdateEditDisplayLogManagement
  );
}
